import './standings.css';

const LISTS = '.standings-strip, .season-strip';
const NUM = /\d+/;
const TWEEN_MS = 500;
const MIN_RESTART_MS = 250; // ~4 Hz: bursts of repaints retarget the running tween

/** Read the first integer in `text`; null if there is none. */
export function parseScore(text: string): number | null {
  const m = NUM.exec(text);
  return m ? Number(m[0]) : null;
}

/** Replace the first integer in `text` with `n` (rounded). */
export function withScore(text: string, n: number): string {
  return text.replace(NUM, String(Math.round(n)));
}

/** Eased value between two scores at progress t (0..1). */
export function tweenValue(from: number, to: number, t: number): number {
  const e = 1 - Math.pow(1 - Math.min(Math.max(t, 0), 1), 3);
  return from + (to - from) * e;
}

interface Tween {
  shown: number;
  target: number;
  from: number;
  start: number;
  raf: number;
}

export function init(root: HTMLElement): void {
  if (typeof MutationObserver === 'undefined') return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const tweens = new WeakMap<Element, Tween>();
  const orders = new WeakMap<Element, string>();
  const tops = new WeakMap<Element, Map<string, number>>();
  const live = new WeakSet<Element>();
  const observed = new WeakSet<Element>();
  const css = getComputedStyle(document.documentElement);
  const easing = css.getPropertyValue('--ds-ease-out').trim() || 'ease-out';
  const flipMs = parseInt(css.getPropertyValue('--ds-dur-slow'), 10) || 350;

  const io =
    typeof IntersectionObserver === 'undefined'
      ? null
      : new IntersectionObserver((entries) => {
          for (const e of entries) {
            if (e.isIntersecting) live.add(e.target);
            else live.delete(e.target);
            syncLive(e.target);
          }
        });

  /** The patcher strips attributes the template lacks, so this is re-applied on every scan. */
  function syncLive(box: Element): void {
    const want = !io || live.has(box);
    if (box.hasAttribute('data-motion-live') !== want) box.toggleAttribute('data-motion-live', want);
  }

  function write(span: Element, text: string): void {
    const node = span.firstChild;
    if (node && node.nodeType === 3) node.nodeValue = text;
    else span.textContent = text;
  }

  function run(span: Element): void {
    const tw = tweens.get(span);
    if (!tw) return;
    const p = (performance.now() - tw.start) / TWEEN_MS;
    const done = p >= 1;
    tw.shown = done ? tw.target : tweenValue(tw.from, tw.target, p);
    write(span, withScore(span.textContent ?? '', tw.shown));
    obs.takeRecords(); // our own writes are not changes
    tw.raf = done ? 0 : requestAnimationFrame(() => run(span));
  }

  function track(span: Element): void {
    const text = span.textContent ?? '';
    const n = parseScore(text);
    if (n === null) return;
    const tw = tweens.get(span);
    if (!tw) {
      tweens.set(span, { shown: n, target: n, from: n, start: 0, raf: 0 });
      return; // first mount: no animation
    }
    if (n === tw.target) {
      // Same final value (e.g. patcher rewrote the text mid-tween): keep the in-flight number.
      if (tw.raf) write(span, withScore(text, tw.shown));
      return;
    }
    if (reduced) {
      tw.shown = tw.target = n;
      return;
    }
    const now = performance.now();
    if (!tw.raf || now - tw.start >= MIN_RESTART_MS) {
      tw.from = tw.shown;
      tw.start = now;
    }
    tw.target = n;
    write(span, withScore(text, tw.shown)); // hold the old number until the next frame
    if (!tw.raf) tw.raf = requestAnimationFrame(() => run(span));
  }

  /** FLIP: rows are already in their new slots; slide each from where it was. */
  function flip(list: Element): void {
    const items = [...list.children] as HTMLElement[];
    const order = items.map((li) => li.getAttribute('data-key') ?? '').join('|');
    const prevTops = tops.get(list);
    const next = new Map<string, number>();
    for (const li of items) next.set(li.getAttribute('data-key') ?? '', li.offsetTop);
    if (!reduced && prevTops && orders.get(list) !== order && typeof items[0]?.animate === 'function') {
      for (const li of items) {
        const key = li.getAttribute('data-key') ?? '';
        const before = prevTops.get(key);
        const d = before === undefined ? 0 : before - (next.get(key) as number);
        if (d) li.animate([{ transform: `translateY(${d}px)` }, { transform: 'none' }], { duration: flipMs, easing });
      }
    }
    orders.set(list, order);
    tops.set(list, next);
  }

  function scan(): void {
    for (const box of root.querySelectorAll(LISTS)) {
      if (io && !observed.has(box)) {
        observed.add(box);
        io.observe(box);
      }
      syncLive(box);
      const list = box.querySelector('.standings-list');
      if (!list) continue;
      for (const span of list.querySelectorAll('.score-num')) track(span);
      flip(list);
    }
  }

  const obs = new MutationObserver(() => {
    scan();
    obs.takeRecords();
  });
  scan();
  obs.observe(root, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['data-motion-live', 'data-key'],
  });
}
